import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { track } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useCart } from "../../contexts/CartContext";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, FileText, ChefHat, UtensilsCrossed, Plus, Clock, Users, Lock } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LangContext";
import { isB2BUser } from "../../lib/roles";
import { ASSET } from "../../lib/api";
import ProductZoom from "../../components/ProductZoom";
import FavoriteButton from "../../components/FavoriteButton";

export default function B2BWineDetail() {
  const { id } = useParams();
  const [wine, setWine] = useState(null);
  const [qty, setQty] = useState(6);
  const [unit, setUnit] = useState("bottle");
  const [aiPricing, setAiPricing] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [loadingQ, setLoadingQ] = useState(false);
  const [pairings, setPairings] = useState(null);
  const [pairRecipe, setPairRecipe] = useState(null);
  const [loadingRecipe, setLoadingRecipe] = useState(false);
  const { add, addMany } = useCart();
  const { user } = useAuth();
  const { lang } = useLang();
  const canSeePallet = isB2BUser(user);

  useEffect(() => {
    api.get(`/wines/${id}`).then(r => { setWine(r.data); track("wine", id, "view"); });
    api.get(`/wines/${id}/pairings`).then(r => setPairings(r.data)).catch(() => {});
  }, [id]);

  if (!wine) return <div className="container-xl py-20">Caricamento…</div>;

  const effectiveUnit = unit === "pallet" && !canSeePallet ? "case" : unit;
  const unitPrice = effectiveUnit === "case" ? wine.price_per_case : effectiveUnit === "pallet" ? wine.price_per_pallet : wine.price_per_bottle;

  const addToCart = () => {
    add({ product_id: wine.id, product_type: "wine", name: `${wine.name} ${wine.vintage}`, image: wine.image, unit_price: unitPrice, quantity: qty, unit: effectiveUnit });
    toast.success("Aggiunto al carrello");
  };

  const requestQuote = async () => {
    if (!user) return toast.error("Accedi per richiedere preventivo");
    setLoadingQ(true);
    try {
      await api.post("/quotes", { wine_ids: [wine.id], quantities: [qty * (unit === "case" ? 6 : unit === "pallet" ? 600 : 1)], note: "Richiesta da scheda prodotto", target_channel: "HORECA" });
      toast.success("Preventivo inviato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setLoadingQ(false); }
  };

  const runPricing = async () => {
    setLoadingAi(true);
    try {
      const r = await api.post(`/wines/${wine.id}/pricing?target_market=HORECA&quantity=${qty}&urgency=normal`);
      setAiPricing(r.data);
    } catch { toast.error("AI error"); } finally { setLoadingAi(false); }
  };

  const generatePairRecipe = async () => {
    setLoadingRecipe(true);
    try {
      const r = await api.post(`/wines/${wine.id}/pair-recipe?lang=${lang}`);
      setPairRecipe(r.data);
    } catch { toast.error("AI recipe error"); } finally { setLoadingRecipe(false); }
  };

  const addAllPairings = () => {
    if (!pairings?.boxes?.length) return;
    addMany(pairings.boxes.map(b => ({
      product_id: b.id, product_type: "box", name: b.name, image: b.image,
      unit_price: b.price, quantity: 1, unit: "pz"
    })));
    toast.success("Box abbinamento aggiunte al carrello");
  };

  return (
    <div data-testid={`b2b-wine-${id}`} className="container-xl py-10">
      <Link to="/b2b/wines" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"><ArrowLeft className="h-4 w-4"/>Catalogo</Link>
      <div className="mt-6 grid md:grid-cols-5 gap-8">
        <div className="md:col-span-2">
          <div className="relative w-full aspect-[4/5] rounded-sm border border-[hsl(var(--border))] bg-white overflow-hidden">
            <ProductZoom src={wine.image} alt={`${wine.name} ${wine.vintage}`} imgClassName="scale-[1.04]" />
            <div className="absolute top-3 left-3 z-10">
              <FavoriteButton
                productType="wine"
                productId={wine.id}
                meta={{ name: wine.name, image: wine.image, price: wine.price_per_bottle }}
              />
            </div>
          </div>
          <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))] text-center uppercase tracking-[0.3em]">
            Clicca sulla bottiglia per ingrandire
          </div>
        </div>
        <div className="md:col-span-3 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))]">{wine.winery} · {wine.denomination}</div>
            <h1 className="editorial-heading text-5xl leading-[0.95] text-[hsl(var(--foreground))] mt-2">{wine.name}</h1>
            <div className="text-[hsl(var(--muted-foreground))] mt-1">{wine.vintage} · {wine.grape} · {wine.region} · {wine.alcohol}% vol</div>
          </div>
          <p className="text-[hsl(var(--muted-foreground))]">{wine.description}</p>
          <div className="flex flex-wrap gap-2">
            {wine.certifications.map((c, i) => <span key={i} className="chip bg-[hsl(var(--muted))] text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/40">{c}</span>)}
            {wine.target_channels.map((c, i) => <span key={`t${i}`} className="chip bg-[hsl(var(--muted))]">{c}</span>)}
          </div>

          <div className="grid grid-cols-3 gap-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm p-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Bottiglia</div>
              <div className="text-2xl font-bold editorial-heading text-[hsl(var(--accent))]">€{wine.price_per_bottle.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Cartone (6)</div>
              <div className="text-2xl font-bold editorial-heading text-[hsl(var(--accent))]">€{wine.price_per_case.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Pallet (600)</div>
              {canSeePallet ? (
                <div className="text-2xl font-bold editorial-heading text-[hsl(var(--accent))]">€{wine.price_per_pallet.toFixed(0)}</div>
              ) : (
                <Link to="/login" data-testid="pallet-locked-cta" className="inline-flex items-center gap-1.5 mt-1 text-sm font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--accent))]">
                  <Lock className="h-3.5 w-3.5" />Solo B2B
                </Link>
              )}
            </div>
          </div>
          {!canSeePallet && (
            <div data-testid="pallet-lock-banner" className="flex items-start gap-3 text-xs bg-[hsl(var(--card))] border border-[hsl(var(--accent))]/30 rounded-sm p-3">
              <Lock className="h-4 w-4 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-[hsl(var(--muted-foreground))]">
                Il prezzo per pallet è riservato agli operatori B2B verificati (broker, importatori, Ho.Re.Ca., GDO).{" "}
                <Link to={user ? "/register" : "/login"} className="text-[hsl(var(--accent))] font-semibold underline">
                  {user ? "Richiedi verifica B2B" : "Accedi come B2B"}
                </Link>
              </div>
            </div>
          )}

          <div className="grid grid-cols-4 gap-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm p-4 text-xs">
            <div><div className="text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Disponibile</div><div className="font-bold text-green-400 text-lg">{wine.stock_available.toLocaleString()}</div></div>
            <div><div className="text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Riservato</div><div className="font-bold text-amber-400 text-lg">{wine.stock_reserved.toLocaleString()}</div></div>
            <div><div className="text-[hsl(var(--muted-foreground))] uppercase tracking-wider">In arrivo</div><div className="font-bold text-blue-400 text-lg">{wine.stock_incoming.toLocaleString()}</div></div>
            <div><div className="text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Bonded</div><div className="font-bold text-[hsl(var(--accent))] text-lg">{wine.stock_bonded.toLocaleString()}</div></div>
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[120px]">
              <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Quantità</label>
              <Input data-testid="b2b-qty" type="number" min={1} value={qty} onChange={e => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="mt-1 bg-[hsl(var(--card))] border-[hsl(var(--border))]" />
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Unità</label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger data-testid="b2b-unit" className="mt-1 bg-[hsl(var(--card))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bottle">Bottiglia</SelectItem>
                  <SelectItem value="case">Cartone</SelectItem>
                  {canSeePallet && <SelectItem value="pallet">Pallet</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="b2b-add-cart" onClick={addToCart} className="bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90 rounded-sm">Aggiungi</Button>
            <Button data-testid="b2b-request-quote" onClick={requestQuote} disabled={loadingQ} variant="outline" className="rounded-sm gap-2 border-[hsl(var(--accent))] text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10"><FileText className="h-4 w-4"/>Preventivo</Button>
          </div>

          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI Pricing</div>
                <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Calcola il prezzo ottimale per il tuo canale</div>
              </div>
              <Button data-testid="b2b-ai-pricing" onClick={runPricing} disabled={loadingAi} size="sm" variant="outline" className="rounded-sm">{loadingAi ? "Analisi…" : "Analizza"}</Button>
            </div>
            {aiPricing && (
              <div data-testid="b2b-ai-pricing-result" className="mt-4 grid md:grid-cols-3 gap-3 text-sm">
                <div><div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Prezzo consigliato/btl</div><div className="text-xl editorial-heading text-[hsl(var(--accent))]">€{(aiPricing.suggested_price_per_bottle || 0).toFixed(2)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Cartone</div><div className="text-xl editorial-heading text-[hsl(var(--accent))]">€{(aiPricing.suggested_price_per_case || 0).toFixed(2)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Margine</div><div className="text-xl editorial-heading text-[hsl(var(--accent))]">{(aiPricing.suggested_margin_pct || 0).toFixed(1)}%</div></div>
                <div className="md:col-span-3 text-[hsl(var(--muted-foreground))] italic">{aiPricing.rationale}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FOOD PAIRINGS + AI RECIPE SECTION (B2C friendly) */}
      <section data-testid="wine-pairings-section" className="mt-16 bg-[hsl(var(--muted))] border border-[hsl(var(--border))] rounded-sm p-6 md:p-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] flex items-center gap-2"><UtensilsCrossed className="h-3.5 w-3.5" /> Abbinamenti · food & ricette</div>
            <h2 className="editorial-heading text-3xl md:text-4xl text-[hsl(var(--foreground))] leading-tight mt-2">
              Cosa cucinare con <span className="italic text-[hsl(var(--accent))]">{wine.name}</span>
            </h2>
            {pairings?.pairing_note && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{pairings.pairing_note}</p>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {pairings?.boxes?.length > 0 && (
              <Button data-testid="pair-add-all" onClick={addAllPairings} variant="outline" size="sm" className="rounded-sm gap-2 border-[hsl(var(--border))]">
                <Plus className="h-4 w-4" />Aggiungi tutte le box
              </Button>
            )}
            <Button data-testid="pair-recipe-btn" onClick={generatePairRecipe} disabled={loadingRecipe} size="sm" className="rounded-sm gap-2 bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90">
              <Sparkles className="h-4 w-4" />{loadingRecipe ? "L'AI cucina…" : "Genera ricetta d'abbinamento"}
            </Button>
          </div>
        </div>

        {/* Suggested food boxes */}
        {pairings?.boxes?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pairings.boxes.map(b => (
              <Link key={b.id} to={`/box/${b.id}`} data-testid={`pair-box-${b.id}`} className="group flex gap-4 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm p-3 hover-lift">
                <img src={b.image} alt={b.name} className="w-24 h-24 object-cover rounded-sm flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--accent))] font-semibold">{b.category} · {b.region || "Italia"}</div>
                  <div className="editorial-heading text-lg text-[hsl(var(--foreground))] leading-tight mt-0.5 line-clamp-2">{b.name}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-base font-bold text-[hsl(var(--accent))]">€{b.price.toFixed(2)}</div>
                    <span className="text-xs text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--accent))]">Scopri →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* AI recipe result */}
        {pairRecipe && (
          <div data-testid="pair-recipe-result" className="mt-8 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm overflow-hidden scroll-fade-in">
            <div className="p-6 md:p-8">
              <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] flex items-center gap-2"><ChefHat className="h-3.5 w-3.5"/>Ricetta generata dall'AI</div>
              <h3 className="editorial-heading text-3xl md:text-4xl leading-[0.95] mt-2 text-[hsl(var(--foreground))]">{pairRecipe.title}</h3>
              {pairRecipe.description && <p className="text-[hsl(var(--muted-foreground))] mt-2">{pairRecipe.description}</p>}
              {pairRecipe.pairing_rationale && (
                <div className="mt-3 border-l-2 border-[hsl(var(--accent))] pl-3 text-sm italic text-[hsl(var(--muted-foreground))]">
                  “{pairRecipe.pairing_rationale}”
                </div>
              )}
              <div className="flex flex-wrap gap-3 mt-4 text-sm">
                <span className="chip bg-[hsl(var(--muted))]"><Clock className="h-3 w-3" />{pairRecipe.prep_time} min</span>
                <span className="chip bg-[hsl(var(--muted))]"><Users className="h-3 w-3" />{pairRecipe.servings} persone</span>
                <span className="chip bg-[hsl(var(--muted))]">{pairRecipe.difficulty}</span>
                {pairRecipe.region && <span className="chip bg-[hsl(var(--muted))]">{pairRecipe.region}</span>}
              </div>
              <div className="mt-6 grid md:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Ingredienti</div>
                  <ul className="space-y-2">
                    {pairRecipe.ingredients.map((ing, i) => (
                      <li key={i} className="flex items-start justify-between gap-3 text-sm border-b border-[hsl(var(--border))] pb-2">
                        <div>
                          <span className="font-medium">{ing.name}</span>
                          <span className="text-[hsl(var(--muted-foreground))]"> — {ing.quantity}</span>
                        </div>
                        {ing.mapped_box_id && <span className="chip bg-[hsl(var(--accent))] text-black">in box</span>}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Preparazione</div>
                  <ol className="space-y-2 text-sm list-decimal ml-5">
                    {pairRecipe.steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                </div>
              </div>
              {pairRecipe.mapped_boxes_detail?.length > 0 && (
                <div className="mt-6 bg-[hsl(var(--muted))] rounded-sm p-4">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Box con gli ingredienti</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {pairRecipe.mapped_boxes_detail.map(b => (
                      <Link key={b.id} to={`/box/${b.id}`} data-testid={`recipe-mapped-box-${b.id}`} className="flex items-center gap-3 bg-[hsl(var(--card))] rounded-sm p-2 hover:shadow-md transition-shadow">
                        <img src={b.image} className="w-12 h-12 object-cover rounded-sm" alt="" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">{b.name}</div>
                          <div className="text-xs text-[hsl(var(--accent))] font-bold">€{b.price.toFixed(2)}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-[hsl(var(--border))] p-4 bg-[hsl(var(--muted))] flex flex-wrap gap-2 justify-end">
              <Button
                data-testid="pair-recipe-add-all"
                size="sm"
                onClick={() => {
                  if (!pairRecipe.mapped_boxes_detail?.length) return toast.error("Nessuna box da aggiungere");
                  addMany(pairRecipe.mapped_boxes_detail.map(b => ({
                    product_id: b.id, product_type: "box", name: b.name, image: b.image,
                    unit_price: b.price, quantity: 1, unit: "pz"
                  })));
                  toast.success("Ingredienti aggiunti al carrello");
                }}
                className="bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90 gap-1 rounded-sm"
              >
                <Plus className="h-4 w-4" />Aggiungi ingredienti al carrello
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
